import { Injectable } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

import { Store } from '@ngrx/store';

import { Dashboard } from '../models/core.models';

import {
  selectDashboardPage,
  loadDashboardViewPreset,
  saveDashboardViewPreset,
} from '../actions/object.actions';

import { AppState } from 'src/app/reducers';
import { selectSelectedPageId } from '../reducers';

import { Observable, take, tap } from 'rxjs';
import { ToastService } from '@services/toast.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class DashboardPageCoordinatorService {
  constructor(
    private store: Store<AppState>,
    private router: Router,
    private translateService: TranslateService,
    private toastService: ToastService,
  ) {}

  // * Initialize page coordination for a dashboard
  // * Implements Cases 1, 2, and 6 from rulebook
  initializeDashboard(
    dashboard: Dashboard,
    route: ActivatedRoute,
  ): Observable<void> {
    return new Observable((subscriber) => {
      const urlPageId = this.extractPageIdFromRoute(route);

      if (urlPageId) {
        // * Case 2: Direct link (URL has pageId) - URL wins
        // * Case 6: Refresh with pageId - same logic applies
        this.handleDirectPageLink(dashboard, urlPageId, route);
      } else {
        // * Case 1: Open dashboard (no pageId in URL) - load saved selection
        // * Case 6: Refresh without pageId - same logic applies
        this.handleDashboardOpen(dashboard, route);
      }

      subscriber.next();
      subscriber.complete();
    });
  }

  // * Handle user page selection via UI
  selectPage(pageId: number, dashboardId: string, route: ActivatedRoute): void {
    // * Case 3: User selects a page (via UI) - Update state, navigate to URL and save to backend
    this.store.dispatch(selectDashboardPage({ pageId }));
    this.saveDashboardViewPreset(dashboardId, pageId);
    this.navigateToPage(pageId, route);
  }

  // * Handle page deletion scenarios
  handlePageDeletion(deletedPageId: number, route: ActivatedRoute): void {
    this.store
      .select(selectSelectedPageId)
      .pipe(
        take(1),
        tap((currentSelectedPageId) => {
          if (currentSelectedPageId === deletedPageId) {
            // * Case 4: Delete active page - wait for reducer to select neighbor, then navigate
            this.handleActivePageDeletion(route);
          } else {
            // * Case 5: Delete non-active page - stay on current page, no URL change
            // NgRx state already updated by deleteDashboardPageSuccess reducer
          }
        }),
      )
      .subscribe();
  }

  // * Handle view preset loading results
  // * Implements saved selection logic with URL sync
  handleViewPresetLoaded(
    pageId: number | undefined,
    dashboard: Dashboard,
    route: ActivatedRoute,
  ): void {
    if (pageId) {
      // * Validate saved pageId still exists
      const pageExists = dashboard.pages.some((page) => page.id === pageId);
      if (pageExists) {
        // * Valid saved selection -> use it and sync URL with replaceUrl
        this.store.dispatch(selectDashboardPage({ pageId }));
        this.navigateToPage(pageId, route, { replaceUrl: true });
      } else {
        // * Case 7: Stale selection - saved pageId no longer exists
        this.handleStaleSelection(dashboard, route);
      }
    } else {
      // * No saved selection -> fallback to first page
      this.fallbackToFirstPage(dashboard, route);
    }
  }

  handlePageOrderChange(dashboard: Dashboard): void {
    // * Case 11: Page order changes (no deletion) - do not change selection
    // * No backend write needed, selection stays unchanged
    // * NgRx state handles the page reordering
  }

  // ! Private helper methods - Start
  private validatePageAccess(
    pageId: number,
    dashboard: Dashboard,
    route: ActivatedRoute,
  ): boolean {
    const pageExists = dashboard.pages.some((page) => page.id === pageId);

    if (!pageExists) {
      // * Case 8: Permission/access revoked or Case 9: Content 404
      this.handleInvalidPage(pageId, dashboard, route);
      return false;
    }

    return true;
  }

  private handleDirectPageLink(
    dashboard: Dashboard,
    pageId: number,
    route: ActivatedRoute,
  ): void {
    if (this.validatePageAccess(pageId, dashboard, route)) {
      // * Valid pageId -> render and save to backend
      this.store.dispatch(selectDashboardPage({ pageId }));
      this.saveDashboardViewPreset(dashboard.id, pageId);
    }
  }

  private handleDashboardOpen(
    dashboard: Dashboard,
    route: ActivatedRoute,
  ): void {
    // * Load saved selection from backend
    this.store.dispatch(loadDashboardViewPreset({ dashboardId: dashboard.id }));
  }

  private handleActivePageDeletion(route: ActivatedRoute): void {
    // * Case 4: Delete active page - reducer already selected neighbor page
    // * Wait for state update, then navigate to the newly selected page
    this.store
      .select(selectSelectedPageId)
      .pipe(
        take(1),
        tap((newSelectedPageId) => {
          if (newSelectedPageId) {
            // * Reducer selected a neighbor page - navigate to it and save selection
            const dashboardId = route.snapshot.params['dashboardId'];
            this.saveDashboardViewPreset(dashboardId, newSelectedPageId);
            this.navigateToPage(newSelectedPageId, route);
          } else {
            // * No pages remain -> navigate to dashboard base (should not happen due to validation)
            this.navigateToDashboardBase(route);
          }
        }),
      )
      .subscribe();
  }

  private handleStaleSelection(
    dashboard: Dashboard,
    route: ActivatedRoute,
  ): void {
    // * Case 7: Stale selection - fallback to first page
    this.fallbackToFirstPage(dashboard, route);
  }

  private handleInvalidPage(
    pageId: number,
    dashboard: Dashboard,
    route: ActivatedRoute,
  ): void {
    // * Cases 8 & 9: Invalid page
    this.toastService.showError(
      this.translateService.instant(
        'dashboard-presentation-preview-mode.message.page-not-available',
      ),
    );

    // * fallback to First Page
    this.fallbackToFirstPage(dashboard, route);
  }

  private fallbackToFirstPage(
    dashboard: Dashboard,
    route: ActivatedRoute,
  ): void {
    const firstPage = dashboard.pages[0];
    if (firstPage) {
      this.store.dispatch(selectDashboardPage({ pageId: firstPage.id }));
      this.saveDashboardViewPreset(dashboard.id, firstPage.id);
      this.navigateToPage(firstPage.id, route, { replaceUrl: true });
    }
  }

  private extractPageIdFromRoute(route: ActivatedRoute): number | null {
    // * Check child route params for pageId
    const pageIdParam = route.firstChild?.snapshot.params['pageId'];
    if (pageIdParam) {
      const pageIdNumber = parseInt(pageIdParam, 10);
      return !isNaN(pageIdNumber) ? pageIdNumber : null;
    }
    return null;
  }

  private navigateToPage(
    pageId: number,
    route: ActivatedRoute,
    options: { replaceUrl?: boolean } = {},
  ): void {
    const workspaceId = route.snapshot.params['workspaceId'];
    const dashboardId = route.snapshot.params['dashboardId'];

    this.router.navigate(
      [
        '/analytics/workspace',
        workspaceId,
        'dashboard',
        dashboardId,
        'page',
        pageId,
      ],
      options,
    );
  }

  private navigateToDashboardBase(route: ActivatedRoute): void {
    const workspaceId = route.snapshot.params['workspaceId'];
    const dashboardId = route.snapshot.params['dashboardId'];

    this.router.navigate([
      '/analytics/workspace',
      workspaceId,
      'dashboard',
      dashboardId,
    ]);
  }

  private saveDashboardViewPreset(dashboardId: string, pageId: number): void {
    this.store.dispatch(
      saveDashboardViewPreset({
        dashboardId,
        pageId,
      }),
    );
  }
  // ! Private helper methods - End
}
